import { Fragment, type ReactNode } from 'react';

const INLINE_TAGS: Record<string, 'strong' | 'em' | 'u' | 's'> = {
  b: 'strong',
  strong: 'strong',
  i: 'em',
  em: 'em',
  u: 'u',
  ins: 'u',
  s: 's',
  strike: 's',
  del: 's',
};
const BLOCK_TAGS = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'tr', 'center', 'section', 'article', 'header', 'footer']);
const LIST_TAGS = new Set(['ul', 'ol']);
const DROP_TAGS = new Set(['script', 'style', 'head', 'title', 'meta', 'link', 'noscript', 'template', 'iframe', 'object', 'embed', 'svg', 'math', 'form', 'button', 'select', 'textarea', 'input']);
const MAX_NODES = 5000;
const MAX_DEPTH = 40;

function safeHref(raw: string | null): string | null {
  if (!raw) return null;
  const href = raw.trim();
  if (/^mailto:/i.test(href)) return href;
  try {
    const url = new URL(href);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

function cleanInline(text: string): string {
  return text.replace(/[​-‍⁠﻿­]/g, '').replace(/\s+/g, ' ');
}

function convert(node: Node, depth: number, budget: { left: number }, key: string): ReactNode {
  if (budget.left-- <= 0 || depth > MAX_DEPTH) return null;
  if (node.nodeType === Node.TEXT_NODE) {
    const text = cleanInline(node.textContent ?? '');
    return text ? text : null;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (DROP_TAGS.has(tag)) return null;
  if (tag === 'br') return <br key={key} />;

  const children = Array.from(el.childNodes)
    .map((child, i) => convert(child, depth + 1, budget, `${key}.${i}`))
    .filter((c) => c !== null && c !== '');
  if (tag === 'a') {
    const href = safeHref(el.getAttribute('href'));
    if (!href) return <Fragment key={key}>{children}</Fragment>;
    return (
      <a key={key} href={href} target="_blank" rel="noopener noreferrer nofollow">
        {children.length ? children : href}
      </a>
    );
  }
  if (children.every((c) => typeof c === 'string' && !c.trim())) return null;
  const inline = INLINE_TAGS[tag];
  if (inline) {
    const Tag = inline;
    return <Tag key={key}>{children}</Tag>;
  }
  if (LIST_TAGS.has(tag)) {
    const Tag = tag as 'ul' | 'ol';
    return <Tag key={key}>{children}</Tag>;
  }
  if (tag === 'li') return <li key={key}>{children}</li>;
  if (tag === 'td' || tag === 'th') return <Fragment key={key}>{children} </Fragment>;
  if (tag === 'blockquote') return <blockquote key={key}>{children}</blockquote>;
  if (BLOCK_TAGS.has(tag)) return <div key={key} className={tag === 'p' ? 'hd-html-p' : undefined}>{children}</div>;
  return <Fragment key={key}>{children}</Fragment>;
}

export function renderMessageHtml(html: string | null | undefined): ReactNode | null {
  if (!html || !html.trim() || typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  if (!doc.body || !(doc.body.textContent ?? '').trim()) return null;
  const budget = { left: MAX_NODES };
  const nodes = Array.from(doc.body.childNodes)
    .map((child, i) => convert(child, 0, budget, String(i)))
    .filter((c) => c !== null && c !== '');
  return nodes.some((c) => typeof c !== 'string' || c.trim()) ? nodes : null;
}
